import { Box, styled } from '@mui/material';
import { useMemo, useState } from 'react';

import {
  AircraftDrawer,
  Carousel,
  CarouselStatic,
  useAircrafts,
  useAirFleetCardLayout,
} from '#features';
import { ELayoutManagerMode, useManifest } from '#shared';

import { useLayoutMode } from '../../LayoutManager';
import { sortByOrder } from '../lib';
import {
  WIDGET_AVAILABILITY_CARD_MAX_WIDTH,
  WIDGET_AVAILABILITY_CARD_MIN_HEIGHT,
  WIDGET_AVAILABILITY_CARD_MIN_WIDTH,
} from '../model';

import { AircraftCardAdapter } from './AircraftCardAdapter';

import type { AircraftData } from '#entities';

const Root = styled(Box, { shouldForwardProp: prop => prop !== 'open' })<{
  open?: boolean;
}>({
  display: 'flex',
  gap: 8,
  flexDirection: 'column',
  flexGrow: 1,
  maxWidth: `100%`,
  margin: 0,
  height: '100%',
});

interface Props {
  maxRows?: number;
  minWidth?: number;
  maxWidth?: number;
  minHeight?: number;
}

export const WidgetCrewAvailabilityContent = ({ maxRows }: Props) => {
  useManifest();
  const { mode } = useLayoutMode();
  const isPreviewMode = mode !== ELayoutManagerMode.RENDER;
  const aircrafts = useAircrafts();
  const [open, setOpen] = useState(false);
  const [selectedAircraft, setSelectedAircraft] = useState<AircraftData>(
    aircrafts[0],
  );

  const aircraftsSorted = useMemo(() => sortByOrder(aircrafts), [aircrafts]);

  const handleSelectAircraft = (aircraft: AircraftData) => {
    setOpen(true);
    setSelectedAircraft(aircraft);
  };

  const handleClose = () => setOpen(false);

  const { rows, colWidth, refCallback } = useAirFleetCardLayout({
    itemsCount: aircraftsSorted.length,
    minColWidth: WIDGET_AVAILABILITY_CARD_MIN_WIDTH,
    maxColWidth: WIDGET_AVAILABILITY_CARD_MAX_WIDTH,
    minRowHeight: WIDGET_AVAILABILITY_CARD_MIN_HEIGHT,
    maxRows: maxRows ?? 0,
    gap: 8,
  });

  const itemProps = {
    width: colWidth,
    onClickId: handleSelectAircraft,
    stretch: false,
  };

  const CarouselComponent = isPreviewMode ? CarouselStatic : Carousel;

  return (
    <Root open={open}>
      <Box ref={refCallback} sx={{ minHeight: 0, flex: 1 }}>
        <CarouselComponent
          renderItem={data => (
            <AircraftCardAdapter data={data} {...itemProps} />
          )}
          items={aircraftsSorted}
          itemKey="Id"
          normalSize={rows}
          gap={8}
          itemWidth={colWidth}
          scrollStepSize={isPreviewMode ? undefined : 1}
          loopInterval={isPreviewMode ? undefined : 8000}
          sx={{ height: '100%' }}
          normalStretch={true}
          normalMinSize={WIDGET_AVAILABILITY_CARD_MIN_HEIGHT}
        />
      </Box>

      <AircraftDrawer
        open={open}
        onClose={handleClose}
        variant="persistent"
        aircraftData={selectedAircraft}
        anchor="right"
      >
        <AircraftDrawer.AircraftEvents />
        <AircraftDrawer.Location />
        <AircraftDrawer.Timeline />
        <AircraftDrawer.Weather />
      </AircraftDrawer>
    </Root>
  );
};
